import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { upsertSettingUser } from "redux/index";
import { ColorPickerProvider, useColorPicker } from "shared/dynamic-imports";
import { IconDispatcher } from "shared/Icons";
import { GenericButton } from "shared/ui-basics/buttons";
import styled, { css } from "styled-components/macro";
import { isDarkColor } from "utils/helpers";

interface CustomColorSelectModalProps {
	isOpen: boolean;
	onClose: () => void;
	selectedColor: string;
	action: (params: { selectedColor: string }) => void;
}

const MAX_ARRAY_LEN = 8;

export const CustomColorSelectModal: React.FC<CustomColorSelectModalProps> = ({ isOpen, onClose, selectedColor, action }) => {
	const { t } = useTranslation();

	const userSettings = useSelector(({ settings }: any) => settings?.userSetting);
	const [currentColor, setCurrentColor] = useState(selectedColor || "#FFFFFF");

	const updateUserSettings = () => {
		if (!userSettings?.colors?.includes(currentColor)) {
			const userSettingsColors = [...userSettings?.colors];
			userSettingsColors?.unshift(currentColor);
			if (userSettingsColors?.length > MAX_ARRAY_LEN) userSettingsColors?.pop();
			upsertSettingUser({
				userSetting: {
					id: userSettings?.id,
					colors: userSettingsColors,
				},
				modifiedAttribNames: ["colors"],
			});
		}
	};

	const confirm = () => {
		onClose();
		updateUserSettings();
		action({ selectedColor: currentColor });
	};

	return (
		<ModalContainer fade={false} isOpen={isOpen} toggle={onClose} size="sm" centered={true}>
			<ColorPickerProvider>
				<ModalHeader toggle={onClose}>{t("SELECT_CUSTOM_COLOR")}</ModalHeader>
				<Body>
					<RenderChromePicker selectedColor={currentColor} setCurrentColor={setCurrentColor} />
					<ColorContainer>
						{userSettings?.colors?.map((color: string) => (
							<Color key={color} color={color} onClick={() => setCurrentColor(color)}>
								<Check selected={currentColor?.toLowerCase() === color?.toLowerCase()} color={isDarkColor(color, true)}>
									{IconDispatcher("CHECK")}
								</Check>
							</Color>
						))}
					</ColorContainer>
				</Body>
				<Footer>
					<GenericButton variant="dark" className="text-capitalized" onClick={confirm}>
						{t("ASPX_12")}
					</GenericButton>
				</Footer>
			</ColorPickerProvider>
		</ModalContainer>
	);
};

interface RenderChromePickerProps {
	selectedColor: string;
	setCurrentColor: (color: string) => void;
}

const RenderChromePicker: React.FC<RenderChromePickerProps> = ({ selectedColor, setCurrentColor }) => {
	const ChromePicker = useColorPicker()?.ChromePicker;
	return <>{ChromePicker && <ChromePicker className="custom-chromePicker" color={selectedColor} onChangeComplete={(color: any) => setCurrentColor(color?.hex)} disableAlpha={true} />}</>;
};

const ModalContainer = styled(Modal)`
	max-width: 281px;
`;
const Body = styled(ModalBody)`
	padding: 16px;
`;

const ColorContainer = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	gap: 8px;
`;

const Color = styled.div<{ color: string }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	flex-shrink: 0;
	border-radius: 4px;
	background: ${({ color }) => color};
	:hover {
		cursor: pointer;
	}
`;

const Check = styled.div<{ selected: boolean; color: string }>`
	visibility: hidden;
	${({ selected }) =>
		selected &&
		css`
			visibility: visible;
		`}
	path {
		fill: ${({ color }) => color};
	}
`;

const Footer = styled(ModalFooter)`
	display: flex;
	align-items: center;
	justify-content: flex-end;
`;
